import { useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import * as apiComms from '../apiComms';
import SideNavBar from '../components/SideNavBar';
import TopNavBar from '../components/TopNavBar';
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

const COUPON_METRICS = [
  { key: 'unclaimedCoupons', label: 'Unclaimed Coupons', detail: 'Created, not yet redeemed' },
  { key: 'claimedCoupons', label: 'Claimed Coupons', detail: 'Redeemed and closed' },
  { key: 'couponsLast30Days', label: 'Coupons / 30 days', detail: 'Created in the last 30 days' },
  { key: 'averageDaysToClaim', label: 'Average Days to Claim', detail: 'Issue to redemption, claimed coupons' },
  { key: 'medianDaysToClaim', label: 'Median Days to Claim', detail: 'Typical issue-to-redemption time' }
];

const COUPON_TYPES = [
  { key: 'nadur2', label: 'NADUR-2', color: '#369fe3' },
  { key: 'nadur6', label: 'NADUR-6', color: '#20c997' },
  { key: 'nadur12', label: 'NADUR-12', color: '#f59f00' }
];

const formatMonth = (month) => {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString('en-IE', {
    month: 'short', year: '2-digit', timeZone: 'UTC'
  });
};

const MonthlyCouponChart = ({ title, data, metric }) => (
  <section style={ styles.chartCard }>
    <h2 style={ styles.panelTitle }>{ title }</h2>
    { data.length > 0 ? (
      <div style={ styles.chartContainer }>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={ data } margin={{ top: 20, right: 18, left: 4, bottom: 8 }}>
            <CartesianGrid stroke='#e5edf2' strokeDasharray='4 4' />
            <XAxis dataKey='month' tickFormatter={ formatMonth } tick={{ fontSize: 12 }} minTickGap={24} />
            <YAxis allowDecimals={false} width={42} tick={{ fontSize: 12 }} />
            <Tooltip labelFormatter={ formatMonth } />
            <Legend />
            { COUPON_TYPES.map((type) => (
              <Area
                key={ type.key }
                type='monotone'
                dataKey={ `${metric}_${type.key}` }
                name={ type.label }
                stackId='coupons'
                stroke={ type.color }
                fill={ type.color }
                fillOpacity={0.72}
                dot={ data.length === 1 }
              />
            )) }
          </AreaChart>
        </ResponsiveContainer>
      </div>
    ) : <p style={ styles.panelText }>No coupon history yet.</p> }
  </section>
);

const UnclaimedCouponAgeing = ({ buckets, total }) => (
  <section style={ styles.chartCard }>
    <h2 style={ styles.panelTitle }>Unclaimed Coupon Ageing</h2>
    <p style={ styles.ageingIntro }>How long currently unclaimed coupons have been waiting since issue.</p>
    <div style={ styles.ageingRows }>
      { buckets.map((bucket) => {
        const percentage = total ? (bucket.count / total) * 100 : 0;
        return (
          <div key={ bucket.label } style={ styles.ageingRow }>
            <span style={ styles.ageingLabel }>{ bucket.label }</span>
            <div style={ styles.ageingTrack } aria-hidden='true'>
              <div style={{ ...styles.ageingBar, width: `${percentage}%`, backgroundColor: bucket.label === '90+ days' ? '#c97814' : '#087f8c' }} />
            </div>
            <span style={ styles.ageingCount }>{ bucket.count.toLocaleString() } <small>({ percentage.toFixed(0) }%)</small></span>
          </div>
        );
      }) }
    </div>
  </section>
);

const ClaimsByStore = ({ stores, total }) => {
  const largestCount = Math.max(1, ...stores.map((store) => store.count));

  return (
    <section style={{ ...styles.chartCard, marginTop: 18 }}>
      <h2 style={ styles.panelTitle }>Claims by Store</h2>
      <p style={ styles.ageingIntro }>Where issued coupons were redeemed. Unknown means the store could not be identified from the transaction reference.</p>
      { stores.length ? (
        <div style={ styles.storeRows }>
          { stores.map((store) => (
            <div key={ store.storeCode } style={ styles.ageingRow }>
              <span style={ styles.ageingLabel }>{ store.storeCode }</span>
              <div style={ styles.ageingTrack } aria-hidden='true'>
                <div style={{
                  ...styles.ageingBar,
                  width: `${store.count / largestCount * 100}%`,
                  backgroundColor: store.storeCode === 'Unknown' ? '#c97814' : '#087f8c'
                }} />
              </div>
              <span style={ styles.ageingCount }>
                { store.count.toLocaleString() } <small>({ (total ? store.count / total * 100 : 0).toFixed(1) }%)</small>
              </span>
            </div>
          )) }
        </div>
      ) : <p style={ styles.panelText }>No claimed coupons yet.</p> }
    </section>
  );
};

const ClaimRateByIssueMonth = ({ data }) => {
  const hasIssuedCoupons = data.some((month) => COUPON_TYPES.some((type) => month[`issued_${type.key}`] > 0));

  return (
    <section style={ styles.chartCard }>
      <h2 style={ styles.panelTitle }>Claim Rate by Issue Month and NADUR Type</h2>
      <p style={ styles.ageingIntro }>
        Percentage of coupons issued in each month that have been claimed. Recent months may rise as customers claim their coupons.
      </p>
      { hasIssuedCoupons ? (
        <div style={ styles.chartContainer }>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={ data } margin={{ top: 20, right: 18, left: 4, bottom: 8 }}>
              <CartesianGrid stroke='#e5edf2' strokeDasharray='4 4' />
              <XAxis dataKey='month' tickFormatter={ formatMonth } tick={{ fontSize: 12 }} minTickGap={24} />
              <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={ (value) => `${value}%` } width={48} tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={ formatMonth }
                formatter={ (value, name, item) => {
                  const typeKey = item.dataKey.replace('rate_', '');
                  const cohort = item.payload;
                  return [`${Number(value).toFixed(1)}% (${cohort[`claimed_${typeKey}`]} of ${cohort[`issued_${typeKey}`]})`, name];
                } }
              />
              <Legend />
              { COUPON_TYPES.map((type) => (
                <Line
                  key={ type.key }
                  type='linear'
                  dataKey={ `rate_${type.key}` }
                  name={ type.label }
                  stroke={ type.color }
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              )) }
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : <p style={ styles.panelText }>No NADUR coupons have been issued yet.</p> }
    </section>
  );
};

const CouponsPage = ({ handleUpdate, activeRoute, isLookup = false }) => {
  const isMobile = useIsMobile();
  const [counts, setCounts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lookupId, setLookupId] = useState('');
  const [lookupError, setLookupError] = useState('');

  const handleLookup = (event) => {
    event.preventDefault();
    const redemptionId = lookupId.trim();
    if (!redemptionId) {
      setLookupError('Enter a coupon ID.');
      return;
    }
    setLookupError('');
    handleUpdate({ updateType: 'navigate', route: '/coupons/lookup/' + encodeURIComponent(redemptionId) });
  };

  useEffect(() => {
    if (isLookup) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiComms.getCouponsOverview().then((result) => {
      if (cancelled) return;
      if (result?.isErr || !result?.response) {
        setError(result?.errMsg ?? 'Unable to load coupon totals.');
      }
      else {
        setCounts(result.response);
      }
    }).catch(() => {
      if (!cancelled) setError('Unable to load coupon totals.');
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [isLookup, refreshKey]);

  return (
    <div style={ styles.page }>
      <TopNavBar handleUpdate={ handleUpdate } activeRoute={ activeRoute } />
      <SideNavBar handleUpdate={ handleUpdate } activeRoute={ activeRoute } />
      <main style={{
        ...styles.main,
        marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT
      }}>
        <div style={ styles.headingRow }>
          <div>
            <div style={ styles.eyebrow }>COUPONS</div>
            <h1 style={ styles.title }>{ isLookup ? 'Coupon Lookup' : 'Coupons Overview' }</h1>
            <p style={ styles.intro }>
              { isLookup
                ? 'Find a redemption code and inspect its history.'
                : 'A snapshot of loyalty rewards earned and redeemed by customers.' }
            </p>
          </div>
          { !isLookup ? (
            <button type='button' style={ styles.refreshButton } onClick={ () => setRefreshKey((key) => key + 1) } disabled={ isLoading }>
              { isLoading ? 'Refreshing...' : 'Refresh totals' }
            </button>
          ) : null }
        </div>

        { isLookup ? (
          <section style={ styles.messagePanel }>
            <h2 style={ styles.panelTitle }>Redemption-code lookup</h2>
            <p style={ styles.panelText }>Enter the coupon ID shown to the customer or store.</p>
            <form onSubmit={ handleLookup } style={ styles.lookupForm }>
              <label htmlFor='couponId' style={ styles.lookupLabel }>Coupon ID</label>
              <div style={ styles.lookupControls }>
                <input
                  id='couponId'
                  type='text'
                  value={ lookupId }
                  onChange={ (event) => { setLookupId(event.target.value); setLookupError(''); } }
                  placeholder='Enter coupon ID'
                  autoComplete='off'
                  style={ styles.lookupInput }
                />
                <button type='submit' style={ styles.lookupButton }>Look up coupon</button>
              </div>
              { lookupError ? <p role='alert' style={ styles.lookupError }>{ lookupError }</p> : null }
            </form>
          </section>
        ) : (
          <>
            { error ? <div role='alert' style={ styles.error }>{ error }</div> : null }
            <div style={ styles.metricGrid }>
              { COUPON_METRICS.map((metric) => (
                <section key={ metric.key } style={ styles.metricCard }>
                  <div style={ styles.metricLabel }>{ metric.label }</div>
                  <div style={ styles.metricValue }>
                    { counts ? (
                      ['averageDaysToClaim', 'medianDaysToClaim'].includes(metric.key)
                        ? (counts[metric.key] == null ? 'N/A' : `${Number(counts[metric.key]).toFixed(1)} days`)
                        : Number(counts[metric.key] ?? 0).toLocaleString()
                    ) : (isLoading ? '...' : '-') }
                  </div>
                  <div style={ styles.metricDetail }>{ metric.detail }</div>
                </section>
              )) }
            </div>
            <p style={ styles.footnote }>
              { counts?.analysisStartDate ? `Includes coupons issued on or after ${new Date(`${counts.analysisStartDate}T00:00:00Z`).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}. ` : '' }
              Claimed means the coupon was redeemed and closed. The 30-day total includes both claimed and unclaimed coupons.
            </p>
            { counts?.unmappedCoupons > 0 ? (
              <p role='status' style={ styles.unmappedWarning }>
                { counts.unmappedCoupons.toLocaleString() } coupon(s) could not be matched to a NADUR card type and are omitted from the chart breakdowns.
              </p>
            ) : null }
            { counts?.unclaimedAgeBuckets?.length ? (
              <UnclaimedCouponAgeing buckets={ counts.unclaimedAgeBuckets } total={ counts.unclaimedCoupons } />
            ) : null }
            { counts?.claimsByStore ? (
              <ClaimsByStore stores={ counts.claimsByStore } total={ counts.claimedCoupons } />
            ) : null }
            { counts ? (
              <div style={ styles.charts }>
                <MonthlyCouponChart title='Issued Coupons Per Month' data={ counts.monthlyCoupons ?? [] } metric='issued' />
                <MonthlyCouponChart title='Claimed Coupons Per Month' data={ counts.monthlyCoupons ?? [] } metric='claimed' />
                {/* 
                { counts.claimRatesByIssueMonth ? <ClaimRateByIssueMonth data={ counts.claimRatesByIssueMonth } /> : null }
                */ }
              </div>
            ) : null }
          </>
        ) }
      </main>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f6f9fb' },
  main: { padding: '74px 22px 32px', minWidth: 0 },
  headingRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 },
  eyebrow: { color: '#087f8c', fontSize: 12, fontWeight: 800, letterSpacing: 1.3, marginBottom: 7 },
  title: { color: '#153243', fontSize: 28, fontWeight: 800, margin: 0 },
  intro: { color: '#596b76', fontSize: 15, margin: '8px 0 0' },
  refreshButton: { border: '1px solid #c8e5f1', borderRadius: 8, backgroundColor: 'white', color: '#087f8c', fontWeight: 700, padding: '10px 14px', cursor: 'pointer' },
  metricGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 16 },
  metricCard: { backgroundColor: 'white', border: '1px solid #dbe7ed', borderRadius: 10, padding: '21px 22px', boxShadow: '0 3px 16px rgba(21, 50, 67, 0.04)' },
  metricLabel: { color: '#495b65', fontSize: 14, fontWeight: 700 },
  metricValue: { color: '#009fe3', fontSize: 'clamp(27px, 2.5vw, 38px)', fontWeight: 800, lineHeight: 1.2, margin: '13px 0 8px', overflowWrap: 'anywhere' },
  metricDetail: { color: '#6a7b85', fontSize: 13 },
  footnote: { color: '#6a7b85', fontSize: 13, marginTop: 18 },
  unmappedWarning: { color: '#8a5400', fontSize: 13, marginTop: 10 },
  ageingIntro: { color: '#596b76', fontSize: 13, margin: '0 0 20px' },
  ageingRows: { display: 'grid', gap: 16, marginBottom: 12 },
  storeRows: { display: 'grid', gap: 16, maxHeight: 420, overflowY: 'auto', paddingRight: 4, marginBottom: 12 },
  ageingRow: { display: 'grid', gridTemplateColumns: 'minmax(90px, 135px) minmax(0, 1fr) minmax(72px, auto)', alignItems: 'center', gap: 12 },
  ageingLabel: { color: '#314955', fontSize: 14, fontWeight: 700 },
  ageingTrack: { height: 12, backgroundColor: '#e5edf2', borderRadius: 6, overflow: 'hidden' },
  ageingBar: { height: '100%', borderRadius: 6 },
  ageingCount: { color: '#153243', fontSize: 14, fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap' },
  charts: { display: 'grid', gap: 18, marginTop: 24 },
  chartCard: { backgroundColor: 'white', border: '1px solid #dbe7ed', borderRadius: 10, padding: '20px 18px 12px', minWidth: 0 },
  chartContainer: { width: '100%', height: 300 },
  error: { border: '1px solid #efc8c8', borderRadius: 8, backgroundColor: '#fff5f5', color: '#9d3434', padding: 12, marginBottom: 16 },
  messagePanel: { backgroundColor: 'white', border: '1px solid #dbe7ed', borderRadius: 10, padding: 24 },
  panelTitle: { color: '#153243', fontSize: 19, fontWeight: 800, margin: '0 0 8px' },
  panelText: { color: '#596b76', margin: 0 },
  lookupForm: { marginTop: 24 },
  lookupLabel: { display: 'block', color: '#314955', fontSize: 14, fontWeight: 700, marginBottom: 8 },
  lookupControls: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  lookupInput: { flex: '1 1 260px', minWidth: 0, border: '1px solid #c6d6de', borderRadius: 8, padding: '11px 13px', fontSize: 16 },
  lookupButton: { border: 0, borderRadius: 8, backgroundColor: '#009fe3', color: 'white', fontWeight: 700, padding: '11px 18px', cursor: 'pointer' },
  lookupError: { color: '#a23636', fontSize: 13, margin: '9px 0 0' }
};

export default CouponsPage;
